import {
  ApplicantAnotherExperience,
  ApplicantArticle,
  ApplicantAward,
  ApplicantCommunity,
  ApplicantContest,
  ApplicantEducation,
  ApplicantMentorship,
  ApplicantOpenSource,
  ApplicantPatent,
  ApplicantPublicTalk,
  ApplicantScientificWork,
} from "@prisma/client";
import Blob from "cross-blob";
import {
  Document,
  HeadingLevel,
  ISectionOptions,
  Packer,
  Paragraph,
  TextRun,
} from "docx";
import flattenDeep from "lodash/flattenDeep";

import {
  ExperienceType,
  getExperienceSectionTitle,
} from "@/lib/experienceType";

import { docStyles } from "./common";
import { numberingStyle } from "./common/numbering";
import { DocGenerator } from "..";
import { renderProseMirrorSchemaToGoogleDoc } from "../proseMirror";
import { ApplicationFull } from "../../prisma";
import { parseProseMirrorSchema } from "../../proseMirror";

interface ExternalProfileDocArgs {
  application: ApplicationFull;
  applicantArticleList: ApplicantArticle[];
  applicantPublicTalkList: ApplicantPublicTalk[];
  applicantContestList: ApplicantContest[];
  applicantAwardList: ApplicantAward[];
  applicantPatentList: ApplicantPatent[];
  applicantOpenSourceList: ApplicantOpenSource[];
  applicantMentorshipList: ApplicantMentorship[];
  applicantTeachingList: ApplicantEducation[];
  applicantCommunityList: ApplicantCommunity[];
  applicantScientificWorkList: ApplicantScientificWork[];
  applicantAnotherExperienceList: ApplicantAnotherExperience[];
}

export const createExternalProfileDocNew: DocGenerator<
  ExternalProfileDocArgs
> = async (
  t,
  {
    application,
    applicantArticleList,
    applicantPublicTalkList,
    applicantContestList,
    applicantAwardList,
    applicantPatentList,
    applicantOpenSourceList,
    applicantMentorshipList,
    applicantTeachingList,
    applicantCommunityList,
    applicantScientificWorkList,
    applicantAnotherExperienceList,
  }
) => {
  const document = new Document({
    styles: docStyles,
    numbering: numberingStyle,
    sections: [
      {
        children: flattenDeep<ISectionOptions["children"][0] | null>([
          new Paragraph({
            children: [
              new TextRun({
                text: "Applicant: ",
              }),
              new TextRun({
                text: application.applicant.fullName
                  ? application.applicant.fullName
                  : `${application.applicant.firstName} ${application.applicant.lastName}`,
              }),
            ],
            heading: HeadingLevel.HEADING_1,
          }),

          new Paragraph({
            style: "wellSpaced",
          }),

          new Paragraph({
            children: [
              new TextRun({
                text: "External Profile",
              }),
            ],
            heading: HeadingLevel.TITLE,
          }),

          ...applicantPublicTalkList
            .map((x): any[] => {
              return [
                new Paragraph({
                  style: "wellSpaced",
                }),
                new Paragraph({
                  style: "wellSpaced",
                }),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: `${t(
                        getExperienceSectionTitle(ExperienceType.PublicTalks)
                      )}: `,
                    }),
                  ],
                  heading: HeadingLevel.HEADING_1,
                }),
                new Paragraph({
                  style: "wellSpaced",
                }),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Conference title: ",
                      bold: true,
                    }),
                    new TextRun({
                      text: x.name ? parseProseMirrorSchema(x.name) : "-",
                    }),
                  ],
                }),
                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Link to website: ",
                      bold: true,
                    }),
                    new TextRun({
                      text: x.url ? parseProseMirrorSchema(x.url) : "-",
                    }),
                  ],
                }),
                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Role: ",
                      bold: true,
                    }),
                    new TextRun({
                      text: x.url ? parseProseMirrorSchema(x.role) : "-",
                    }),
                  ],
                }),
                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Year: ",
                      bold: true,
                    }),
                    new TextRun({
                      text: x.year ? String(x.year) : "-",
                    }),
                  ],
                }),

                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Any famous pros next?: ",
                      bold: true,
                    }),
                  ],
                }),

                renderProseMirrorSchemaToGoogleDoc(x.famousProsNext, "-"),

                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Any significant numbers?: ",
                      bold: true,
                    }),
                  ],
                }),

                renderProseMirrorSchemaToGoogleDoc(x.significantStats, "-"),

                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Number of attendees: ",
                      bold: true,
                    }),
                    new TextRun({
                      text: x.statsAttendees ? String(x.statsAttendees) : "-",
                    }),
                  ],
                }),

                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Number of online viewers: ",
                      bold: true,
                    }),
                    new TextRun({
                      text: x.statsOnlineViewers
                        ? String(x.statsOnlineViewers)
                        : "-",
                    }),
                  ],
                }),

                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Number of speakers: ",
                      bold: true,
                    }),
                    new TextRun({
                      text: x.statsSpeakers ? String(x.statsSpeakers) : "-",
                    }),
                  ],
                }),

                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Budget: ",
                      bold: true,
                    }),
                    new TextRun({
                      text: x.statsBudget ? String(x.statsBudget) : "-",
                    }),
                  ],
                }),

                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Number of sponsors or partners: ",
                      bold: true,
                    }),
                    new TextRun({
                      text: x.statsSponsorsPartners
                        ? String(x.statsSponsorsPartners)
                        : "-",
                    }),
                  ],
                }),

                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Number of publications about the event: ",
                      bold: true,
                    }),
                    new TextRun({
                      text: x.statsPublications
                        ? String(x.statsPublications)
                        : "-",
                    }),
                  ],
                }),
              ];
            })
            .reduce((prev, curr) => prev.concat(curr), []),

          ...applicantArticleList
            .map((x): any[] => {
              return [
                new Paragraph({
                  style: "wellSpaced",
                }),
                new Paragraph({
                  style: "wellSpaced",
                }),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: `${t(
                        getExperienceSectionTitle(ExperienceType.Articles)
                      )}: `,
                    }),
                  ],
                  heading: HeadingLevel.HEADING_1,
                }),
                new Paragraph({
                  style: "wellSpaced",
                }),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Article title: ",
                      bold: true,
                    }),
                    new TextRun({
                      text: x.name ? parseProseMirrorSchema(x.name) : "-",
                    }),
                  ],
                }),
                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Link to article: ",
                      bold: true,
                    }),
                    new TextRun({
                      text: x.url ? parseProseMirrorSchema(x.url) : "-",
                    }),
                  ],
                }),
                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Role: ",
                      bold: true,
                    }),
                    new TextRun({
                      text: x.url ? parseProseMirrorSchema(x.role) : "-",
                    }),
                  ],
                }),

                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Brief description: ",
                      bold: true,
                    }),
                  ],
                }),

                renderProseMirrorSchemaToGoogleDoc(x.description, "-"),

                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Type of publication: ",
                      bold: true,
                    }),
                  ],
                }),

                renderProseMirrorSchemaToGoogleDoc(x.typeOfPublication, "-"),
                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Year: ",
                      bold: true,
                    }),
                    new TextRun({
                      text: x.year ? String(x.year) : "-",
                    }),
                  ],
                }),

                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Were you the only one who participated/was mentioned?: ",
                      bold: true,
                    }),
                  ],
                }),

                renderProseMirrorSchemaToGoogleDoc(x.onlyYouParticipated, "-"),

                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Why were you particularly, and not other experts, invited to take part in the activity or mentioned in it? ",
                      bold: true,
                    }),
                  ],
                }),

                renderProseMirrorSchemaToGoogleDoc(x.whyYouMentioned, "-"),

                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Tell us more:",
                      bold: true,
                    }),
                  ],
                }),

                renderProseMirrorSchemaToGoogleDoc(x.tellUsMore, "-"),

                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Any significant numbers?: ",
                      bold: true,
                    }),
                  ],
                }),

                renderProseMirrorSchemaToGoogleDoc(x.significantStats, "-"),

                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Number of viewers, listeners, readers: ",
                      bold: true,
                    }),
                    new TextRun({
                      text: x.statsViewersListeners
                        ? String(x.statsViewersListeners)
                        : "-",
                    }),
                  ],
                }),

                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Number of likes: ",
                      bold: true,
                    }),
                    new TextRun({
                      text: x.statsLikes ? String(x.statsLikes) : "-",
                    }),
                  ],
                }),

                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Number of comments: ",
                      bold: true,
                    }),
                    new TextRun({
                      text: x.statsComments ? String(x.statsComments) : "-",
                    }),
                  ],
                }),

                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Number of reposts: ",
                      bold: true,
                    }),
                    new TextRun({
                      text: x.statsReposts ? String(x.statsReposts) : "-",
                    }),
                  ],
                }),

                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Number of quotations: ",
                      bold: true,
                    }),
                    new TextRun({
                      text: x.statsQuotations ? String(x.statsQuotations) : "-",
                    }),
                  ],
                }),

                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Number of mentions: ",
                      bold: true,
                    }),
                    new TextRun({
                      text: x.statsMentions ? String(x.statsMentions) : "-",
                    }),
                  ],
                }),
              ];
            })
            .reduce((prev, curr) => prev.concat(curr), []),

          ...applicantScientificWorkList
            .map((x): any[] => {
              return [
                new Paragraph({
                  style: "wellSpaced",
                }),
                new Paragraph({
                  style: "wellSpaced",
                }),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: `${t(
                        getExperienceSectionTitle(
                          ExperienceType.ScientificWorkOrPhD
                        )
                      )}: `,
                    }),
                  ],
                  heading: HeadingLevel.HEADING_1,
                }),
                new Paragraph({
                  style: "wellSpaced",
                }),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Scientific work title: ",
                      bold: true,
                    }),
                    new TextRun({
                      text: x.name ? parseProseMirrorSchema(x.name) : "-",
                    }),
                  ],
                }),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Link to your dissertation: ",
                      bold: true,
                    }),
                    new TextRun({
                      text: x.name ? parseProseMirrorSchema(x.url) : "-",
                    }),
                  ],
                }),
                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Period: ",
                      bold: true,
                    }),
                    new TextRun({
                      text:
                        x.fromYear && x.toYear
                          ? `${String(x.fromYear)} - ${
                              Number(x.toYear) === 2099
                                ? "Now"
                                : String(x.toYear)
                            }}`
                          : "-",
                    }),
                  ],
                }),
                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Name of University or other scientific institution",
                      bold: true,
                    }),
                  ],
                }),

                renderProseMirrorSchemaToGoogleDoc(x.nameOfUniversity, "-"),

                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Brief description: ",
                      bold: true,
                    }),
                  ],
                }),

                renderProseMirrorSchemaToGoogleDoc(x.description, "-"),

                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Any significant numbers?: ",
                      bold: true,
                    }),
                  ],
                }),

                renderProseMirrorSchemaToGoogleDoc(x.significantStats, "-"),

                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Average grade for courses and exams: ",
                      bold: true,
                    }),
                    new TextRun({
                      text: x.statsAverageGrade
                        ? String(x.statsAverageGrade)
                        : "-",
                    }),
                  ],
                }),

                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Number of published articles during studies: ",
                      bold: true,
                    }),
                    new TextRun({
                      text: x.statsPublishedArticles
                        ? String(x.statsPublishedArticles)
                        : "-",
                    }),
                  ],
                }),

                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Impact factor of publications: ",
                      bold: true,
                    }),
                    new TextRun({
                      text: x.statsImpactFactor
                        ? String(x.statsImpactFactor)
                        : "-",
                    }),
                  ],
                }),

                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Number of presentations at conferences during studies: ",
                      bold: true,
                    }),
                    new TextRun({
                      text: x.statsPresentations
                        ? String(x.statsPresentations)
                        : "-",
                    }),
                  ],
                }),

                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Number of teaching or research assistantships: ",
                      bold: true,
                    }),
                    new TextRun({
                      text: x.statsTeachingResearch
                        ? String(x.statsTeachingResearch)
                        : "-",
                    }),
                  ],
                }),

                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Number of grants obtained during studies: ",
                      bold: true,
                    }),
                    new TextRun({
                      text: x.statsGrantsObtained
                        ? String(x.statsGrantsObtained)
                        : "-",
                    }),
                  ],
                }),

                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Amount of funding within grants: ",
                      bold: true,
                    }),
                    new TextRun({
                      text: x.statsAmountOfFunding
                        ? String(x.statsAmountOfFunding)
                        : "-",
                    }),
                  ],
                }),
              ];
            })
            .reduce((prev, curr) => prev.concat(curr), []),

          ...applicantContestList
            .filter((x) => !x.isOrganizer)
            .map((x): any[] => {
              return [
                new Paragraph({
                  style: "wellSpaced",
                }),
                new Paragraph({
                  style: "wellSpaced",
                }),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: `${t(
                        getExperienceSectionTitle(ExperienceType.Contests)
                      )}: `,
                    }),
                  ],
                  heading: HeadingLevel.HEADING_1,
                }),
                new Paragraph({
                  style: "wellSpaced",
                }),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Event title: ",
                      bold: true,
                    }),
                    new TextRun({
                      text: x.name ? parseProseMirrorSchema(x.name) : "-",
                    }),
                  ],
                }),
                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Link to website / pictures: ",
                      bold: true,
                    }),
                    new TextRun({
                      text: x.url ? parseProseMirrorSchema(x.url) : "-",
                    }),
                  ],
                }),
                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Role: ",
                      bold: true,
                    }),
                    new TextRun({
                      text: x.url ? parseProseMirrorSchema(x.role) : "-",
                    }),
                  ],
                }),
                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Year: ",
                      bold: true,
                    }),
                    new TextRun({
                      text: x.year ? String(x.year) : "-",
                    }),
                  ],
                }),

                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Any significant numbers?: ",
                      bold: true,
                    }),
                  ],
                }),

                renderProseMirrorSchemaToGoogleDoc(x.significantStats, "-"),

                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Number of attendees: ",
                      bold: true,
                    }),
                    new TextRun({
                      text: x.statsAttendees ? String(x.statsAttendees) : "-",
                    }),
                  ],
                }),

                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Number of online viewers: ",
                      bold: true,
                    }),
                    new TextRun({
                      text: x.statsOnlineViewers
                        ? String(x.statsOnlineViewers)
                        : "-",
                    }),
                  ],
                }),

                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Number of entries submitted: ",
                      bold: true,
                    }),
                    new TextRun({
                      text: x.statsEntriesSubmitted
                        ? String(x.statsEntriesSubmitted)
                        : "-",
                    }),
                  ],
                }),

                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Number of winners: ",
                      bold: true,
                    }),
                    new TextRun({
                      text: x.statsWinners ? String(x.statsWinners) : "-",
                    }),
                  ],
                }),

                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Number of sponsors or partners: ",
                      bold: true,
                    }),
                    new TextRun({
                      text: x.statsSponsorsPartners
                        ? String(x.statsSponsorsPartners)
                        : "-",
                    }),
                  ],
                }),

                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Number of publications about the contest: ",
                      bold: true,
                    }),
                    new TextRun({
                      text: x.statsPublicationsContest
                        ? String(x.statsPublicationsContest)
                        : "-",
                    }),
                  ],
                }),

                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Budget: ",
                      bold: true,
                    }),
                    new TextRun({
                      text: x.statsBudget ? String(x.statsBudget) : "-",
                    }),
                  ],
                }),

                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Prize fund: ",
                      bold: true,
                    }),
                    new TextRun({
                      text: x.statsPrizeFund ? String(x.statsPrizeFund) : "-",
                    }),
                  ],
                }),

                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "What place did you get?: ",
                      bold: true,
                    }),
                  ],
                }),

                renderProseMirrorSchemaToGoogleDoc(x.place, "-"),

                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "What prize did you receive?: ",
                      bold: true,
                    }),
                  ],
                }),

                renderProseMirrorSchemaToGoogleDoc(x.achievements, "-"),
              ];
            })
            .reduce((prev, curr) => prev.concat(curr), []),

          ...applicantAwardList
            .map((x): any[] => {
              return [
                new Paragraph({
                  style: "wellSpaced",
                }),
                new Paragraph({
                  style: "wellSpaced",
                }),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: `${t(
                        getExperienceSectionTitle(ExperienceType.Award)
                      )}: `,
                    }),
                  ],
                  heading: HeadingLevel.HEADING_1,
                }),
                new Paragraph({
                  style: "wellSpaced",
                }),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Award title: ",
                      bold: true,
                    }),
                    new TextRun({
                      text: x.name ? parseProseMirrorSchema(x.name) : "-",
                    }),
                  ],
                }),
                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Link to website: ",
                      bold: true,
                    }),
                    new TextRun({
                      text: x.url ? parseProseMirrorSchema(x.url) : "-",
                    }),
                  ],
                }),
                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Brief description: ",
                      bold: true,
                    }),
                  ],
                }),

                renderProseMirrorSchemaToGoogleDoc(x.description, "-"),
                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Any significant numbers?: ",
                      bold: true,
                    }),
                  ],
                }),

                renderProseMirrorSchemaToGoogleDoc(x.significantStats, "-"),

                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Number of nominations: ",
                      bold: true,
                    }),
                    new TextRun({
                      text: x.statsNominations
                        ? String(x.statsNominations)
                        : "-",
                    }),
                  ],
                }),

                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Number of nominees: ",
                      bold: true,
                    }),
                    new TextRun({
                      text: x.statsNominees ? String(x.statsNominees) : "-",
                    }),
                  ],
                }),

                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Year: ",
                      bold: true,
                    }),
                    new TextRun({
                      text: x.year ? String(x.year) : "-",
                    }),
                  ],
                }),
              ];
            })
            .reduce((prev, curr) => prev.concat(curr), []),

          ...applicantCommunityList
            .map((x): any[] => {
              return [
                new Paragraph({
                  style: "wellSpaced",
                }),
                new Paragraph({
                  style: "wellSpaced",
                }),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: `${t(
                        getExperienceSectionTitle(
                          ExperienceType.CommunityDevelopment
                        )
                      )}: `,
                    }),
                  ],
                  heading: HeadingLevel.HEADING_1,
                }),
                new Paragraph({
                  style: "wellSpaced",
                }),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Title: ",
                      bold: true,
                    }),
                    new TextRun({
                      text: x.name ? parseProseMirrorSchema(x.name) : "-",
                    }),
                  ],
                }),
                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Link to the community: ",
                      bold: true,
                    }),
                    new TextRun({
                      text: x.url ? parseProseMirrorSchema(x.url) : "-",
                    }),
                  ],
                }),
                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Period: ",
                      bold: true,
                    }),
                    new TextRun({
                      text:
                        x.fromYear && x.toYear
                          ? `${String(x.fromYear)} - ${
                              Number(x.toYear) === 2099
                                ? "Now"
                                : String(x.toYear)
                            }}`
                          : "-",
                    }),
                  ],
                }),
                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Community goal: ",
                      bold: true,
                    }),
                  ],
                }),

                renderProseMirrorSchemaToGoogleDoc(x.communityGoal, "-"),

                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Number of participants: ",
                      bold: true,
                    }),
                  ],
                }),

                renderProseMirrorSchemaToGoogleDoc(x.numbersOfAttendee, "-"),

                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Who is this community for?: ",
                      bold: true,
                    }),
                  ],
                }),

                renderProseMirrorSchemaToGoogleDoc(x.whoIsCommunityFor, "-"),

                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Any significant numbers?: ",
                      bold: true,
                    }),
                  ],
                }),

                renderProseMirrorSchemaToGoogleDoc(x.significantStats, "-"),
              ];
            })
            .reduce((prev, curr) => prev.concat(curr), []),

          ...applicantTeachingList
            .map((x): any[] => {
              return [
                new Paragraph({
                  style: "wellSpaced",
                }),
                new Paragraph({
                  style: "wellSpaced",
                }),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: `${t(
                        getExperienceSectionTitle(
                          ExperienceType.TeachingAndMentorship
                        )
                      )}: `,
                    }),
                  ],
                  heading: HeadingLevel.HEADING_1,
                }),
                new Paragraph({
                  style: "wellSpaced",
                }),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Title: ",
                      bold: true,
                    }),
                    new TextRun({
                      text: x.name ? parseProseMirrorSchema(x.name) : "-",
                    }),
                  ],
                }),
                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Link to activity: ",
                      bold: true,
                    }),
                    new TextRun({
                      text: x.url ? parseProseMirrorSchema(x.url) : "-",
                    }),
                  ],
                }),
                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Period: ",
                      bold: true,
                    }),
                    new TextRun({
                      text:
                        x.fromYear && x.toYear
                          ? `${String(x.fromYear)} - ${
                              Number(x.toYear) === 2099
                                ? "Now"
                                : String(x.toYear)
                            }}`
                          : "-",
                    }),
                  ],
                }),
                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Brief description: ",
                      bold: true,
                    }),
                  ],
                }),

                renderProseMirrorSchemaToGoogleDoc(x.description, "-"),
              ];
            })
            .reduce((prev, curr) => prev.concat(curr), []),

          ...applicantMentorshipList
            .map((x): any[] => {
              return [
                new Paragraph({
                  style: "wellSpaced",
                }),
                new Paragraph({
                  style: "wellSpaced",
                }),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: `${t(
                        getExperienceSectionTitle(
                          ExperienceType.TeachingAndMentorship
                        )
                      )}: `,
                    }),
                  ],
                  heading: HeadingLevel.HEADING_1,
                }),
                new Paragraph({
                  style: "wellSpaced",
                }),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Title: ",
                      bold: true,
                    }),
                    new TextRun({
                      text: x.name ? parseProseMirrorSchema(x.name) : "-",
                    }),
                  ],
                }),
                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Link to activity: ",
                      bold: true,
                    }),
                    new TextRun({
                      text: x.url ? parseProseMirrorSchema(x.url) : "-",
                    }),
                  ],
                }),
                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Teaching or mentoring term: ",
                      bold: true,
                    }),
                    new TextRun({
                      text: x.url ? parseProseMirrorSchema(x.terms) : "-",
                    }),
                  ],
                }),
                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Period: ",
                      bold: true,
                    }),
                    new TextRun({
                      text:
                        x.fromYear && x.toYear
                          ? `${String(x.fromYear)} - ${
                              Number(x.toYear) === 2099
                                ? "Now"
                                : String(x.toYear)
                            }}`
                          : "-",
                    }),
                  ],
                }),
                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "What did you teach: ",
                      bold: true,
                    }),
                  ],
                }),

                renderProseMirrorSchemaToGoogleDoc(x.what, "-"),

                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Where did you teach: ",
                      bold: true,
                    }),
                  ],
                }),

                renderProseMirrorSchemaToGoogleDoc(x.where, "-"),

                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Who did you teach: ",
                      bold: true,
                    }),
                  ],
                }),

                renderProseMirrorSchemaToGoogleDoc(x.who, "-"),

                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Tell us more: ",
                      bold: true,
                    }),
                  ],
                }),

                renderProseMirrorSchemaToGoogleDoc(x.tellUsMore, "-"),

                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Number of students: ",
                      bold: true,
                    }),
                  ],
                }),

                renderProseMirrorSchemaToGoogleDoc(x.studentsNumbers, "-"),

                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Brief description: ",
                      bold: true,
                    }),
                  ],
                }),

                renderProseMirrorSchemaToGoogleDoc(x.description, "-"),

                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Metrics: ",
                      bold: true,
                    }),
                  ],
                }),

                renderProseMirrorSchemaToGoogleDoc(x.significantStats, "-"),
              ];
            })
            .reduce((prev, curr) => prev.concat(curr), []),

          ...applicantOpenSourceList
            .map((x): any[] => {
              return [
                new Paragraph({
                  style: "wellSpaced",
                }),
                new Paragraph({
                  style: "wellSpaced",
                }),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: `${t(
                        getExperienceSectionTitle(
                          ExperienceType.OpenSourceContribution
                        )
                      )}: `,
                    }),
                  ],
                  heading: HeadingLevel.HEADING_1,
                }),
                new Paragraph({
                  style: "wellSpaced",
                }),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Title: ",
                      bold: true,
                    }),
                    new TextRun({
                      text: x.name ? parseProseMirrorSchema(x.name) : "-",
                    }),
                  ],
                }),
                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Link to the repo: ",
                      bold: true,
                    }),
                    new TextRun({
                      text: x.url ? parseProseMirrorSchema(x.url) : "-",
                    }),
                  ],
                }),
                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Period: ",
                      bold: true,
                    }),
                    new TextRun({
                      text:
                        x.fromYear && x.toYear
                          ? `${String(x.fromYear)} - ${
                              Number(x.toYear) === 2099
                                ? "Now"
                                : String(x.toYear)
                            }}`
                          : "-",
                    }),
                  ],
                }),
                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Brief description: ",
                      bold: true,
                    }),
                  ],
                }),

                renderProseMirrorSchemaToGoogleDoc(x.description, "-"),

                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Your role: ",
                      bold: true,
                    }),
                  ],
                }),

                renderProseMirrorSchemaToGoogleDoc(x.yourRole, "-"),

                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Any significant numbers?: ",
                      bold: true,
                    }),
                  ],
                }),

                renderProseMirrorSchemaToGoogleDoc(x.significantStats, "-"),

                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Number of stars: ",
                      bold: true,
                    }),
                    new TextRun({
                      text: x.statsStars ? String(x.statsStars) : "-",
                    }),
                  ],
                }),

                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Number of forks: ",
                      bold: true,
                    }),
                    new TextRun({
                      text: x.statsForks ? String(x.statsForks) : "-",
                    }),
                  ],
                }),

                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Number of downloads: ",
                      bold: true,
                    }),
                    new TextRun({
                      text: x.statsDownloads ? String(x.statsDownloads) : "-",
                    }),
                  ],
                }),

                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Number of contributions: ",
                      bold: true,
                    }),
                    new TextRun({
                      text: x.statsContributions
                        ? String(x.statsContributions)
                        : "-",
                    }),
                  ],
                }),
              ];
            })
            .reduce((prev, curr) => prev.concat(curr), []),

          ...applicantPatentList
            .map((x): any[] => {
              return [
                new Paragraph({
                  style: "wellSpaced",
                }),
                new Paragraph({
                  style: "wellSpaced",
                }),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: `${t(
                        getExperienceSectionTitle(ExperienceType.Patent)
                      )}: `,
                    }),
                  ],
                  heading: HeadingLevel.HEADING_1,
                }),
                new Paragraph({
                  style: "wellSpaced",
                }),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Patent title: ",
                      bold: true,
                    }),
                    new TextRun({
                      text: x.name ? parseProseMirrorSchema(x.name) : "-",
                    }),
                  ],
                }),
                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Link to website: ",
                      bold: true,
                    }),
                    new TextRun({
                      text: x.url ? parseProseMirrorSchema(x.url) : "-",
                    }),
                  ],
                }),
                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Year: ",
                      bold: true,
                    }),
                    new TextRun({
                      text: x.year ? String(x.year) : "-",
                    }),
                  ],
                }),
                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Brief description: ",
                      bold: true,
                    }),
                  ],
                }),

                renderProseMirrorSchemaToGoogleDoc(x.description, "-"),
              ];
            })
            .reduce((prev, curr) => prev.concat(curr), []),

          ...applicantAnotherExperienceList
            .map((x): any[] => {
              return [
                new Paragraph({
                  style: "wellSpaced",
                }),
                new Paragraph({
                  style: "wellSpaced",
                }),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: `${t(
                        getExperienceSectionTitle(
                          ExperienceType.AnotherExperience
                        )
                      )}: `,
                    }),
                  ],
                  heading: HeadingLevel.HEADING_1,
                }),
                new Paragraph({
                  style: "wellSpaced",
                }),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Title: ",
                      bold: true,
                    }),
                    new TextRun({
                      text: x.name ? parseProseMirrorSchema(x.name) : "-",
                    }),
                  ],
                }),
                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Link: ",
                      bold: true,
                    }),
                    new TextRun({
                      text: x.url ? parseProseMirrorSchema(x.url) : "-",
                    }),
                  ],
                }),
                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Period: ",
                      bold: true,
                    }),
                    new TextRun({
                      text:
                        x.fromYear && x.toYear
                          ? `${String(x.fromYear)} - ${
                              Number(x.toYear) === 2099
                                ? "Now"
                                : String(x.toYear)
                            }}`
                          : "-",
                    }),
                  ],
                }),
                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Brief description: ",
                      bold: true,
                    }),
                  ],
                }),

                renderProseMirrorSchemaToGoogleDoc(x.description, "-"),
              ];
            })
            .reduce((prev, curr) => prev.concat(curr), []),
        ]).filter((x): x is ISectionOptions["children"][0] => x !== null),
      },
    ],
  });

  return new Blob([await Packer.toBuffer(document)]);
};
